import { ViewPlugin, Decoration, WidgetType } from '@codemirror/view';
import { syntaxTree } from '@codemirror/language';

// Widget for rendered horizontal rule
class HRWidget extends WidgetType {
  toDOM() {
    const el = document.createElement('hr');
    el.style.border = 'none';
    el.style.borderTop = '1px solid #e2e8f0';
    el.style.margin = '16px 0';
    return el;
  }
}

// Widget for blockquote marker replacement
class BlockquoteMarkerWidget extends WidgetType {
  toDOM() {
    const el = document.createElement('span');
    el.style.borderLeft = '3px solid #3B82F6';
    el.style.paddingLeft = '4px';
    el.textContent = '';
    return el;
  }
}

// Widget for rendered link
class LinkWidget extends WidgetType {
  constructor(text, url) {
    super();
    this.text = text;
    this.url = url;
  }
  toDOM() {
    const el = document.createElement('a');
    el.textContent = this.text;
    el.href = this.url;
    el.style.color = '#3B82F6';
    el.style.textDecoration = 'underline';
    el.style.textDecorationColor = '#93c5fd';
    el.style.cursor = 'pointer';
    el.onclick = (e) => { e.preventDefault(); };
    return el;
  }
}

// Widget for rendered image with click-to-select and resize
class ImageWidget extends WidgetType {
  constructor(alt, url, width, from, to) {
    super();
    this.alt = alt;
    this.url = url;
    this.width = width; // null = auto, number = px
    this.from = from;   // doc position start
    this.to = to;       // doc position end
  }
  eq(other) {
    return this.url === other.url && this.alt === other.alt && this.width === other.width;
  }
  toDOM(view) {
    const wrapper = document.createElement('span');
    wrapper.className = 'cm-image-widget';
    wrapper.style.display = 'block';
    wrapper.style.margin = '8px 0';
    wrapper.style.position = 'relative';
    wrapper.style.maxWidth = '100%';
    if (this.width) {
      wrapper.style.width = this.width + 'px';
    }

    const img = document.createElement('img');
    img.src = this.url;
    img.alt = this.alt;
    img.style.width = '100%';
    img.style.borderRadius = '6px';
    img.style.display = 'block';
    img.style.cursor = 'pointer';
    img.loading = 'lazy';
    img.onerror = () => {
      img.style.display = 'none';
      const fallback = document.createElement('span');
      fallback.textContent = `[이미지: ${this.alt || this.url}]`;
      fallback.style.color = '#9ca3af';
      fallback.style.fontStyle = 'italic';
      wrapper.appendChild(fallback);
    };

    // Click to select (deselect others via shared class)
    wrapper.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      document.querySelectorAll('.cm-image-widget.selected').forEach(el => {
        if (el !== wrapper) el.classList.remove('selected');
      });
      wrapper.classList.toggle('selected');
    });

    // Resize handle (bottom-right corner)
    const handle = document.createElement('span');
    handle.className = 'cm-image-resize-handle';

    let startX = 0, startWidth = 0;
    const widget = this;

    handle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      startX = e.clientX;
      startWidth = wrapper.offsetWidth;
      wrapper.classList.add('resizing');

      const onMouseMove = (e) => {
        const newWidth = Math.max(80, startWidth + (e.clientX - startX));
        wrapper.style.width = newWidth + 'px';
      };

      const onMouseUp = (e) => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        wrapper.classList.remove('resizing');

        const finalWidth = Math.max(80, startWidth + (e.clientX - startX));
        // Find current position of this image in the doc (positions may have shifted)
        if (view) {
          const doc = view.state.doc.toString();
          const oldUrl = widget.width
            ? `${widget.url} =${widget.width}x`
            : widget.url;
          const oldMarkdown = `![${widget.alt}](${oldUrl})`;
          const pos = doc.indexOf(oldMarkdown);
          if (pos >= 0) {
            const newUrl = `${widget.url} =${finalWidth}x`;
            const newText = `![${widget.alt}](${newUrl})`;
            view.dispatch({
              changes: { from: pos, to: pos + oldMarkdown.length, insert: newText }
            });
          }
        }
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    });

    wrapper.appendChild(img);
    wrapper.appendChild(handle);
    return wrapper;
  }
  ignoreEvent() { return false; }
}

// Widget for checkbox
class CheckboxWidget extends WidgetType {
  constructor(checked) {
    super();
    this.checked = checked;
  }
  eq(other) { return this.checked === other.checked; }
  toDOM() {
    const el = document.createElement('input');
    el.type = 'checkbox';
    el.checked = this.checked;
    el.disabled = true;
    el.style.marginRight = '6px';
    el.style.verticalAlign = 'middle';
    el.style.accentColor = '#3B82F6';
    return el;
  }
}

// Helper: add pipe character decorations for a table line
function addPipeDecorations(state, line, decorations) {
  const text = line.text;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '|') {
      const pos = line.from + i;
      decorations.push(Decoration.mark({
        attributes: { class: 'cm-table-pipe' }
      }).range(pos, pos + 1));
    }
  }
}

// Create decorations — always render preview, highlight cursor line with subtle bg
function buildDecorations(view) {
  const decorations = [];
  const { state } = view;
  const cursorLine = state.doc.lineAt(state.selection.main.head).number;

  // Add subtle background to the active line
  if (view.hasFocus) {
    const activeLine = state.doc.line(cursorLine);
    decorations.push(Decoration.line({
      attributes: {
        class: 'cm-live-active-line'
      }
    }).range(activeLine.from));
  }

  syntaxTree(state).iterate({
    enter(node) {

      // ATX Headings - match pdv-content sizes
      if (node.name === 'ATXHeading1' || node.name === 'ATXHeading2' || node.name === 'ATXHeading3' ||
          node.name === 'ATXHeading4' || node.name === 'ATXHeading5' || node.name === 'ATXHeading6') {
        const level = parseInt(node.name.replace('ATXHeading', ''));
        const sizes = { 1: '26px', 2: '22px', 3: '18px', 4: '16px', 5: '14px', 6: '14px' };
        const weights = { 1: '700', 2: '700', 3: '600', 4: '600', 5: '600', 6: '600' };

        // Find the HeaderMark (the # symbols)
        const line = state.doc.lineAt(node.from);
        const lineText = line.text;
        const match = lineText.match(/^(#{1,6})\s/);
        if (match) {
          const markEnd = line.from + match[0].length;
          // Hide the # marks
          decorations.push(Decoration.replace({}).range(node.from, markEnd));

          // Style the heading text
          if (markEnd < node.to) {
            decorations.push(Decoration.mark({
              attributes: {
                style: `font-size: ${sizes[level]}; font-weight: ${weights[level]}; color: #1d1d1f; line-height: 1.3;`
              }
            }).range(markEnd, node.to));
          }
        }
      }

      // Bold (**text** or __text__)
      if (node.name === 'StrongEmphasis') {
        const text = state.doc.sliceString(node.from, node.to);
        const wrapLen = text.startsWith('**') ? 2 : (text.startsWith('__') ? 2 : 2);
        const openEnd = node.from + wrapLen;
        const closeStart = node.to - wrapLen;

        if (openEnd < closeStart) {
          decorations.push(Decoration.replace({}).range(node.from, openEnd));
          decorations.push(Decoration.replace({}).range(closeStart, node.to));
          decorations.push(Decoration.mark({
            attributes: { style: 'font-weight: 700;' }
          }).range(openEnd, closeStart));
        }
      }

      // Italic (*text* or _text_)
      if (node.name === 'Emphasis') {
        const openEnd = node.from + 1;
        const closeStart = node.to - 1;

        if (openEnd < closeStart) {
          decorations.push(Decoration.replace({}).range(node.from, openEnd));
          decorations.push(Decoration.replace({}).range(closeStart, node.to));
          decorations.push(Decoration.mark({
            attributes: { style: 'font-style: italic;' }
          }).range(openEnd, closeStart));
        }
      }

      // Inline code
      if (node.name === 'InlineCode') {
        const text = state.doc.sliceString(node.from, node.to);
        if (text.startsWith('`') && text.endsWith('`')) {
          decorations.push(Decoration.replace({}).range(node.from, node.from + 1));
          decorations.push(Decoration.replace({}).range(node.to - 1, node.to));
          decorations.push(Decoration.mark({
            attributes: {
              style: 'background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-family: "SF Mono", "Fira Code", monospace; font-size: 0.9em; color: #e11d48;'
            }
          }).range(node.from + 1, node.to - 1));
        }
      }

      // Blockquote marker (>)
      if (node.name === 'QuoteMark') {
        decorations.push(Decoration.replace({
          widget: new BlockquoteMarkerWidget()
        }).range(node.from, node.to));
      }

      // Blockquote content styling
      if (node.name === 'Blockquote') {
        const line = state.doc.lineAt(node.from);
        decorations.push(Decoration.line({
          attributes: {
            style: 'border-left: 3px solid #3B82F6; padding-left: 12px; color: #6b7280;'
          }
        }).range(line.from));
      }

      // Strikethrough (~~text~~)
      if (node.name === 'Strikethrough') {
        const text = state.doc.sliceString(node.from, node.to);
        if (text.startsWith('~~') && text.endsWith('~~') && text.length > 4) {
          decorations.push(Decoration.replace({}).range(node.from, node.from + 2));
          decorations.push(Decoration.replace({}).range(node.to - 2, node.to));
          decorations.push(Decoration.mark({
            attributes: { style: 'text-decoration: line-through; color: #9ca3af;' }
          }).range(node.from + 2, node.to - 2));
        }
      }

      // Image ![alt](url) or ![alt](url =WIDTHx) — render as actual image with resize
      if (node.name === 'Image') {
        const fullText = state.doc.sliceString(node.from, node.to);
        const imgMatch = fullText.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
        if (imgMatch) {
          const rawUrl = imgMatch[2];
          // Parse optional width: "url =300x" or "url =300x200"
          const sizeMatch = rawUrl.match(/^(.+?)\s+=(\d+)x\d*\s*$/);
          const url = sizeMatch ? sizeMatch[1] : rawUrl;
          const width = sizeMatch ? parseInt(sizeMatch[2]) : null;
          decorations.push(Decoration.replace({
            widget: new ImageWidget(imgMatch[1], url, width, node.from, node.to)
          }).range(node.from, node.to));
        }
      }

      // Links [text](url)
      if (node.name === 'Link') {
        let linkText = '';
        let linkUrl = '';
        let hasChildren = false;

        const cursor = node.node.cursor();
        if (cursor.firstChild()) {
          do {
            if (cursor.name === 'LinkLabel') {
              linkText = state.doc.sliceString(cursor.from + 1, cursor.to - 1);
            }
            if (cursor.name === 'URL') {
              linkUrl = state.doc.sliceString(cursor.from, cursor.to);
              hasChildren = true;
            }
          } while (cursor.nextSibling());
        }

        if (hasChildren && linkText) {
          decorations.push(Decoration.replace({
            widget: new LinkWidget(linkText, linkUrl)
          }).range(node.from, node.to));
        }
      }

      // Horizontal rule
      if (node.name === 'HorizontalRule') {
        decorations.push(Decoration.replace({
          widget: new HRWidget()
        }).range(node.from, node.to));
      }

      // Table — visual table styling when cursor is outside
      if (node.name === 'Table') {
        const tableStartLine = state.doc.lineAt(node.from).number;
        const tableEndLine = state.doc.lineAt(node.to).number;
        const cursorInTable = cursorLine >= tableStartLine && cursorLine <= tableEndLine;

        if (!cursorInTable) {
          // Iterate child nodes for header, separator, rows
          let dataRowIdx = 0;
          const cursor = node.node.cursor();
          if (cursor.firstChild()) {
            do {
              if (cursor.name === 'TableHeader') {
                const hdrLine = state.doc.lineAt(cursor.from);
                decorations.push(Decoration.line({
                  attributes: { class: 'cm-table-line cm-table-header' }
                }).range(hdrLine.from));
                // Style pipes in header
                addPipeDecorations(state, hdrLine, decorations);
              }

              if (cursor.name === 'TableDelimiter') {
                // Full separator line (|------|------|)
                const sepLine = state.doc.lineAt(cursor.from);
                decorations.push(Decoration.line({
                  attributes: { class: 'cm-table-line cm-table-separator' }
                }).range(sepLine.from));
              }

              if (cursor.name === 'TableRow') {
                const rowLine = state.doc.lineAt(cursor.from);
                const rowClass = dataRowIdx % 2 === 1 ? 'cm-table-line cm-table-row cm-table-row-even' : 'cm-table-line cm-table-row';
                decorations.push(Decoration.line({
                  attributes: { class: rowClass }
                }).range(rowLine.from));
                addPipeDecorations(state, rowLine, decorations);
                dataRowIdx++;
              }
            } while (cursor.nextSibling());
          }
        }
        return false; // don't descend into table children again
      }
    }
  });

  // Regex-based decorations for elements not in syntax tree
  // Checkbox lists: - [ ] and - [x]
  for (let i = 1; i <= state.doc.lines; i++) {
    const line = state.doc.line(i);
    const checkMatch = line.text.match(/^(\s*[-*+]\s)\[([ x])\]\s/);
    if (checkMatch) {
      const isChecked = checkMatch[2] === 'x';
      const markerStart = line.from + checkMatch[1].length;
      const markerEnd = markerStart + 3; // [x] or [ ]
      decorations.push(Decoration.replace({
        widget: new CheckboxWidget(isChecked)
      }).range(markerStart, markerEnd + 1)); // include trailing space
    }
  }

  // Sort decorations by position (required by CodeMirror)
  decorations.sort((a, b) => a.from - b.from || a.startSide - b.startSide);

  return Decoration.set(decorations, true);
}

export const livePreviewPlugin = ViewPlugin.fromClass(class {
  constructor(view) {
    this.decorations = buildDecorations(view);
  }
  update(update) {
    if (update.docChanged || update.selectionSet) {
      this.decorations = buildDecorations(update.view);
    }
  }
}, {
  decorations: v => v.decorations
});
